/**
 * ArabicRemixes.jsx — dedicated topical hub for the Arabic-remix search
 * cluster. Lives at /arabic-remixes (English) and /ar/arabic-remixes
 * (Arabic). Not a copy of /music: this page is an editorial +
 * discovery surface that routes users into the real existing catalog.
 *
 * SEO contract:
 *   • Unique <title>, description, canonical, OG/Twitter.
 *   • Reciprocal EN/AR hreflang (self + opposite + x-default → EN).
 *   • Semantic H1 + H2/H3; exactly one H1 per locale.
 *   • index,follow,max-image-preview:large.
 *   • CollectionPage + BreadcrumbList + ItemList JSON-LD (ItemList is
 *     emitted only when real catalog tracks are present).
 *   • NO fabricated ratings / reviews / offers / popularity.
 *
 * Data contract:
 *   • Live catalog pulled via /api/tracks and /api/tracks/genres.
 *   • Regional cards shown only if the matching genre has >=1 live
 *     tracks today — orphan links are not emitted.
 *   • Blog feed pulled via /api/blog?limit=6.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { TrackCard, useTrackActions } from "@/components/CatalogPrimitives";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import { api } from "@/lib/api";

const PRODUCTION_ORIGIN = "https://djspyofficial.com";

// Arabic Unicode block covers Arabic, Arabic Supplement, and
// Arabic Presentation Forms A/B. One Arabic character in the title,
// artist name, or title_ar is enough for an "Arabic song" signal.
const ARABIC_SCRIPT_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

// Edit-category values recognised as regional-Arabic signals. Must
// exactly match the backend's EDIT_CATEGORIES normalisation
// (see backend/server.py).
const REGIONAL_EDIT_CATEGORIES = new Set([
  "khaleeji", "iraqi", "masri", "lebanese", "syrian", "maghribi",
]);

// Edit-category chips supported by the backend. Dabke/oriental are
// not currently separate backend categories, so they are not emitted
// as data-driven cards unless real tracks start appearing with those
// edit_category values.
const REGIONAL_CARDS = [
  { key: "khaleeji", edit_category: "khaleeji" },
  { key: "iraqi",    edit_category: "iraqi" },
  { key: "lebanese", edit_category: "lebanese" },
  { key: "maghribi", edit_category: "maghribi" },
  { key: "masri",    edit_category: "masri" },     // Egyptian
  { key: "syrian",   edit_category: "syrian" },
];

// DJ-edit / remix track types that belong on an Arabic Remix /
// DJ Edit discovery hub. Original productions are also accepted so
// the hub doesn't drop a bona-fide Arabic release just because the
// track type is "original".
const HUB_TRACK_TYPES = new Set(["remix", "mashup", "edit", "funky", "original"]);

/**
 * Deterministic "is this track an Arabic release?" helper.
 *
 * Signals (any one is sufficient):
 *   1. Arabic script in title / title_ar / artist.
 *   2. edit_category matches a known Arabic region.
 *
 * Genre is NOT a signal — Moombahton, Trap, Reggaeton etc. describe
 * production style, not language or cultural origin, and a track may
 * be Arabic AND Moombahton at the same time (and often is).
 *
 * Does NOT scan `description`, `about`, SEO blurbs, or any long-form
 * marketing copy — those fields can accidentally mention Arabic in
 * an English release's blog write-up.
 */
function isArabicRelevant(t) {
  if (!t) return false;
  const title = String(t.title || "");
  const titleAr = String(t.title_ar || "");
  const artist = String(t.artist || "");
  if (ARABIC_SCRIPT_RE.test(title) || ARABIC_SCRIPT_RE.test(titleAr) || ARABIC_SCRIPT_RE.test(artist)) {
    return true;
  }
  const ec = String(t.edit_category || "").toLowerCase().trim();
  if (ec && REGIONAL_EDIT_CATEGORIES.has(ec)) return true;
  return false;
}

function injectMeta(name, content, attr = "name") {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function injectLink(rel, attrs) {
  const sel = `link[rel="${rel}"]${Object.entries(attrs)
    .filter(([k]) => k !== "href")
    .map(([k, v]) => `[${k}="${v}"]`)
    .join("")}`;
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    for (const [k, v] of Object.entries(attrs)) if (k !== "href") el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  if (attrs.href) el.setAttribute("href", attrs.href);
  return el;
}
function removeMeta(selector) {
  if (typeof document === "undefined") return;
  document.querySelectorAll(selector).forEach((n) => n.remove());
}

function tracksAreArabic(t) {
  return isArabicRelevant(t);
}

export default function ArabicRemixes() {
  const { t } = useTranslation();
  const { lang, isRTL } = useLocale();
  const [tracks, setTracks] = useState(null);   // null = loading, [] = empty
  const [regionalCounts, setRegionalCounts] = useState({});
  const [posts, setPosts] = useState([]);
  const actions = useTrackActions();

  // ── Data load ───────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tr, bl] = await Promise.all([
          api.get("/tracks?limit=500"),
          api.get("/blog?limit=20").catch(() => ({ data: { items: [] } })),
        ]);
        if (cancelled) return;
        const liveTracks = Array.isArray(tr.data) ? tr.data : (tr.data?.items || []);
        const arabic = liveTracks
          .filter(isArabicRelevant)
          .filter((row) => !row.track_type || HUB_TRACK_TYPES.has(String(row.track_type).toLowerCase()));
        // Order: remix / mashup / edit first, then by newest if date available
        const typeRank = { remix: 0, mashup: 1, edit: 2, funky: 3, original: 4 };
        arabic.sort((a, b) => {
          const ra = typeRank[String(a.track_type || "").toLowerCase()] ?? 9;
          const rb = typeRank[String(b.track_type || "").toLowerCase()] ?? 9;
          if (ra !== rb) return ra - rb;
          return String(b.created_at || "").localeCompare(String(a.created_at || ""));
        });
        setTracks(arabic.slice(0, 12));

        // Regional counts — strictly derived from edit_category on the
        // SAME tracks that already qualify as Arabic-relevant. If no
        // Arabic track carries a regional edit_category, the entire
        // "Explore Arabic Styles" section will stay hidden.
        const counts = {};
        for (const row of arabic) {
          // Only edit/funky types are listed under /music/edits — count what the card will show.
          if (!["edit", "funky"].includes(String(row.track_type || "").toLowerCase())) continue;
          const ec = String(row.edit_category || "").toLowerCase().trim();
          if (ec && REGIONAL_EDIT_CATEGORIES.has(ec)) counts[ec] = (counts[ec] || 0) + 1;
        }
        setRegionalCounts(counts);

        const postItems = Array.isArray(bl.data) ? bl.data : (bl.data?.items || []);
        const relevant = postItems
          .filter((p) => {
            const title = String(p.title || "") + " " + String(p.title_ar || "") + " " + String(p.track_title || "");
            return ARABIC_SCRIPT_RE.test(title);
          })
          .slice(0, 6);
        setPosts(relevant.length ? relevant : postItems.slice(0, 6));
      } catch {
        if (!cancelled) {
          setTracks([]);
          setRegionalCounts({});
          setPosts([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── SEO head ────────────────────────────────────────────────────────
  useEffect(() => {
    const enPath = "/arabic-remixes";
    const enUrl = `${PRODUCTION_ORIGIN}${enPath}`;
    const arUrl = `${PRODUCTION_ORIGIN}/ar${enPath}`;
    const selfUrl = lang === "ar" ? arUrl : enUrl;

    const title = t("arabicRemixes.seo.title");
    const description = t("arabicRemixes.seo.description");
    document.title = title;
    injectMeta("description", description);
    injectMeta("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    injectMeta("og:type", "website", "property");
    injectMeta("og:title", title, "property");
    injectMeta("og:description", description, "property");
    injectMeta("og:url", selfUrl, "property");
    injectMeta("og:locale", lang === "ar" ? "ar_AE" : "en_US", "property");
    injectMeta("twitter:card", "summary_large_image");
    injectMeta("twitter:title", title);
    injectMeta("twitter:description", description);

    // Canonical + reciprocal hreflang. Clear any residual tags first.
    removeMeta('link[rel="alternate"][hreflang]');
    injectLink("canonical", { href: selfUrl });
    injectLink("alternate", { hreflang: "en",        href: enUrl });
    injectLink("alternate", { hreflang: "ar",        href: arUrl });
    injectLink("alternate", { hreflang: "x-default", href: enUrl });
  }, [lang, t]);

  // ── Regional cards — only show a card if the live Arabic subset has
  // ── at least one track that carries that edit_category. If none of
  // ── our Arabic tracks are regionally classified yet, the whole
  // ── "Explore Arabic Styles" section stays hidden rather than being
  // ── padded with generic production genres.
  const liveRegionals = useMemo(
    () => REGIONAL_CARDS.filter((c) => (regionalCounts[c.edit_category] || 0) > 0),
    [regionalCounts],
  );

  // JSON-LD
  useEffect(() => {
    const enPath = "/arabic-remixes";
    const url = lang === "ar"
      ? `${PRODUCTION_ORIGIN}/ar${enPath}`
      : `${PRODUCTION_ORIGIN}${enPath}`;
    const name = t("arabicRemixes.seo.title");
    const description = t("arabicRemixes.seo.description");

    const graph = [
      {
        "@type": "CollectionPage",
        name,
        description,
        url,
        inLanguage: lang === "ar" ? "ar" : "en",
        isPartOf: { "@type": "WebSite", url: PRODUCTION_ORIGIN, name: "DJ SPY Official" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: lang === "ar" ? "الرئيسية" : "Home", item: `${PRODUCTION_ORIGIN}${lang === "ar" ? "/ar" : ""}` },
          { "@type": "ListItem", position: 2, name: lang === "ar" ? "الموسيقى" : "Music", item: `${PRODUCTION_ORIGIN}${lang === "ar" ? "/ar/music" : "/music"}` },
          { "@type": "ListItem", position: 3, name, item: url },
        ],
      },
    ];
    if (tracks && tracks.length > 0) {
      graph.push({
        "@type": "ItemList",
        itemListElement: tracks.slice(0, 10).map((tr, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${PRODUCTION_ORIGIN}${lang === "ar" ? "/ar" : ""}/track/${tr.slug || tr.id}`,
          name: tr.title,
        })),
      });
    }
    const payload = { "@context": "https://schema.org", "@graph": graph };
    let el = document.querySelector('script[data-ld="arabic-remixes"]');
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-ld", "arabic-remixes");
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);
    return () => {
      const stale = document.querySelector('script[data-ld="arabic-remixes"]');
      if (stale) stale.remove();
    };
  }, [tracks, lang, t]);

  const regionalLabel = (key) => t(`arabicRemixes.regional.${key}.label`);
  const regionalBlurb = (key) => t(`arabicRemixes.regional.${key}.blurb`);

  return (
    <div className="min-h-screen bg-black text-white" dir={isRTL ? "rtl" : "ltr"} data-testid="arabic-remixes-page">
      <Nav />
      <main className="pt-28 pb-24">
        {/* 1. HERO ───────────────────────────────────────────────── */}
        <section className="px-5 md:px-10 max-w-[1400px] mx-auto">
          <p className="font-mono text-[11px] tracking-[0.3em] text-red-brand" data-testid="arabic-remixes-eyebrow">
            {t("arabicRemixes.hero.eyebrow")}
          </p>
          <h1 className="font-anton text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]" data-testid="arabic-remixes-h1">
            {t("arabicRemixes.hero.h1")}
          </h1>
          <p className="text-white/70 text-base md:text-lg mt-5 max-w-3xl" data-testid="arabic-remixes-intro">
            {t("arabicRemixes.hero.intro")}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to={localisedTo("/music", lang)}
              className="btn-primary"
              data-testid="arabic-remixes-cta-primary"
            >
              {t("arabicRemixes.hero.ctaPrimary")}
            </Link>
            <Link
              to={localisedTo("/music/edits", lang)}
              className="btn-ghost"
              data-testid="arabic-remixes-cta-secondary"
            >
              {t("arabicRemixes.hero.ctaSecondary")}
            </Link>
          </div>
        </section>

        {/* 2. FEATURED / LATEST ARABIC RELEASES ──────────────────── */}
        <section className="px-5 md:px-10 max-w-[1400px] mx-auto mt-20" data-testid="arabic-remixes-featured">
          <h2 className="font-anton text-3xl md:text-4xl">{t("arabicRemixes.featured.heading")}</h2>
          <p className="text-white/60 mt-3 max-w-2xl">{t("arabicRemixes.featured.intro")}</p>
          <div className="mt-8">
            {tracks === null && (
              <div className="hairline rounded-2xl p-8 text-center text-white/40" data-testid="arabic-remixes-tracks-loading">
                {t("arabicRemixes.featured.loading")}
              </div>
            )}
            {tracks !== null && tracks.length === 0 && (
              <div className="hairline rounded-2xl p-8 text-center text-white/60" data-testid="arabic-remixes-tracks-empty">
                <p>{t("arabicRemixes.featured.emptyBody")}</p>
                <Link to={localisedTo("/music", lang)} className="btn-primary inline-block mt-5" data-testid="arabic-remixes-empty-cta">
                  {t("arabicRemixes.featured.emptyCta")}
                </Link>
              </div>
            )}
            {tracks && tracks.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="arabic-remixes-tracks-grid">
                {tracks.map((tr, i) => (
                  <TrackCard
                    key={tr.id || tr.slug || i}
                    track={tr}
                    index={i}
                    playingId={actions.playingId}
                    onPlayToggle={actions.onPlayToggle}
                    onBuy={actions.onBuy}
                    onDownload={actions.onDownload}
                    onAddToCart={actions.onAddToCart}
                    buying={actions.buying}
                    inCart={actions.inCart}
                    downloadingId={actions.downloadingId}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* 3. EXPLORE ARABIC STYLES — strictly data-driven. Shown only
            when at least one Arabic-relevant track has a matching
            edit_category. No generic style fallback. */}
        {liveRegionals.length > 0 && (
          <section className="px-5 md:px-10 max-w-[1400px] mx-auto mt-20" data-testid="arabic-remixes-styles">
            <h2 className="font-anton text-3xl md:text-4xl">{t("arabicRemixes.styles.heading")}</h2>
            <p className="text-white/60 mt-3 max-w-2xl">{t("arabicRemixes.styles.intro")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-8" data-testid="arabic-remixes-regional-grid">
              {liveRegionals.map((c) => (
                <Link
                  key={c.key}
                  to={localisedTo(`/music/edits?edit_category=${c.edit_category}`, lang)}
                  className="hairline rounded-2xl p-5 hover:border-red-brand transition group"
                  data-testid={`arabic-remixes-regional-${c.key}`}
                >
                  <p className="font-display text-lg text-white">{regionalLabel(c.key)}</p>
                  <p className="text-white/50 text-sm mt-2 leading-snug">{regionalBlurb(c.key)}</p>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-red-brand mt-4 group-hover:underline">
                    {t("arabicRemixes.styles.explore")} →
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* 4. FOR PROFESSIONAL DJS ────────────────────────────── */}
        <section className="px-5 md:px-10 max-w-[1400px] mx-auto mt-20" data-testid="arabic-remixes-pros">
          <h2 className="font-anton text-3xl md:text-4xl">{t("arabicRemixes.pros.heading")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
            {["preview", "delivery", "dj_ready", "regional", "secure", "collections"].map((key) => (
              <div key={key} className="hairline rounded-2xl p-6" data-testid={`arabic-remixes-pros-${key}`}>
                <p className="font-mono text-[11px] tracking-[0.3em] text-red-brand">
                  {t(`arabicRemixes.pros.items.${key}.eyebrow`)}
                </p>
                <h3 className="font-display text-xl text-white mt-2">{t(`arabicRemixes.pros.items.${key}.title`)}</h3>
                <p className="text-white/60 text-sm mt-3 leading-relaxed">{t(`arabicRemixes.pros.items.${key}.body`)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 5. REGIONAL ARABIC REMIX DISCOVERY — editorial copy ───── */}
        <section className="px-5 md:px-10 max-w-[1200px] mx-auto mt-24" data-testid="arabic-remixes-editorial">
          <h2 className="font-anton text-3xl md:text-4xl">{t("arabicRemixes.editorial.heading")}</h2>
          <div className="prose prose-invert prose-headings:font-display max-w-none mt-6 space-y-10">
            {["khaleeji", "iraqi", "egyptian", "lebanese", "maghribi"].map((region) => (
              <div key={region} data-testid={`arabic-remixes-editorial-${region}`}>
                <h3 className="font-display text-2xl text-white">{t(`arabicRemixes.editorial.${region}.heading`)}</h3>
                <p className="text-white/70 leading-relaxed mt-3">{t(`arabicRemixes.editorial.${region}.body`)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 6. LATEST STORIES ─────────────────────────────────────── */}
        {posts.length > 0 && (
          <section className="px-5 md:px-10 max-w-[1400px] mx-auto mt-20" data-testid="arabic-remixes-stories">
            <div className="flex items-end justify-between">
              <h2 className="font-anton text-3xl md:text-4xl">{t("arabicRemixes.stories.heading")}</h2>
              <Link
                to={localisedTo("/blog", lang)}
                className="font-mono text-[11px] tracking-[0.3em] text-red-brand hover:underline"
                data-testid="arabic-remixes-blog-all"
              >
                {t("arabicRemixes.stories.viewAll")} →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
              {posts.map((p) => (
                <Link
                  key={p.slug}
                  to={localisedTo(`/blog/${p.slug}`, lang)}
                  className="hairline rounded-2xl p-5 hover:border-red-brand transition"
                  data-testid={`arabic-remixes-blog-${p.slug}`}
                >
                  <p className="font-mono text-[10px] tracking-[0.25em] text-white/40">{(p.genre || p.track_genre || "Story").toString().toUpperCase()}</p>
                  <h3 className="font-display text-lg text-white mt-2 leading-snug">{p.title}</h3>
                  {p.summary && <p className="text-white/50 text-sm mt-3 line-clamp-3">{p.summary}</p>}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* 7. FAQ ─────────────────────────────────────────────────── */}
        <section className="px-5 md:px-10 max-w-[1000px] mx-auto mt-24" data-testid="arabic-remixes-faq">
          <h2 className="font-anton text-3xl md:text-4xl">{t("arabicRemixes.faq.heading")}</h2>
          <div className="mt-6 divide-y divide-white/10 hairline rounded-2xl">
            {["types", "preview", "download", "regional", "free", "request"].map((key) => (
              <details key={key} className="group p-5" data-testid={`arabic-remixes-faq-${key}`}>
                <summary className="cursor-pointer font-display text-base text-white flex justify-between items-center">
                  <span>{t(`arabicRemixes.faq.items.${key}.q`)}</span>
                  <span className="font-mono text-xs text-red-brand group-open:rotate-45 transition">+</span>
                </summary>
                <p className="text-white/60 text-sm mt-3 leading-relaxed">{t(`arabicRemixes.faq.items.${key}.a`)}</p>
              </details>
            ))}
          </div>
        </section>

        {/* 8. FINAL CTA ───────────────────────────────────────────── */}
        <section className="px-5 md:px-10 max-w-[1400px] mx-auto mt-24" data-testid="arabic-remixes-final-cta">
          <div className="hairline rounded-3xl p-8 md:p-12 text-center">
            <h2 className="font-anton text-3xl md:text-5xl">{t("arabicRemixes.finalCta.heading")}</h2>
            <p className="text-white/60 mt-4 max-w-2xl mx-auto">{t("arabicRemixes.finalCta.body")}</p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to={localisedTo("/music", lang)} className="btn-primary" data-testid="final-cta-browse-music">
                {t("arabicRemixes.finalCta.browseMusic")}
              </Link>
              <Link to={localisedTo("/music/premium", lang)} className="btn-ghost" data-testid="final-cta-premium">
                {t("arabicRemixes.finalCta.premium")}
              </Link>
              <Link to={localisedTo("/music/free", lang)} className="btn-ghost" data-testid="final-cta-free">
                {t("arabicRemixes.finalCta.free")}
              </Link>
              <Link to={localisedTo("/music/edits", lang)} className="btn-ghost" data-testid="final-cta-edits">
                {t("arabicRemixes.finalCta.edits")}
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
